import { MessageDirection, type SupportTimelineItemRecord } from '@navio/nsdk';
import { keepPreviousData } from '@tanstack/react-query';
import { noddiAsync, URLKeys } from 'noddi-async';
import { cn, cva, NoddiIcon } from 'noddi-ui-common';
import {
  ApiErrorMessageInternal,
  NoddiBadge,
  NoddiCard,
  NoddiEmptyContent,
  NoddiLink,
  NoddiSpinner,
  TablePagination,
  usePaginationQueryParam
} from 'noddi-ui-web';
import { adminRoutes, NoddiDateFormats, noddiFormatDate } from 'noddi-util';
import type { ReactNode } from 'react';
import { supportChannelMeta, supportDirectionMeta } from './channelMeta';

type Props = {
  userGroupId: number;
  /** When true, rows deep-link into the admin Support inbox conversation. */
  linkToInbox?: boolean;
  title?: string;
  description?: string;
  headerNode?: ReactNode;
  className?: string;
};

const channelBadgeVariants = cva('flex size-8 shrink-0 items-center justify-center rounded-lg', {
  variants: {
    channel: {
      EMAIL: 'bg-muted text-muted-foreground',
      SMS: 'bg-card-light-background text-primary',
      WIDGET: 'bg-muted text-muted-foreground',
      VOICE: 'bg-card-light-background text-primary'
    }
  }
});

/**
 * Channel-badged Support message feed for a user-group profile (B6).
 * One row per message — never collapses channel-bound conversations.
 */
export const SupportTimelineFeed = ({
  userGroupId,
  linkToInbox = false,
  title = 'Support',
  description = 'Email, SMS, widget, and voice messages for this customer',
  headerNode,
  className
}: Props) => {
  const { pagination, setPagination } = usePaginationQueryParam({ paramKeyPrefix: 'support-timeline' });

  const { data, isPending, error, refetch, isFetching } = noddiAsync.useGet({
    type: URLKeys.getPaginatedUserGroupSupportTimeline,
    input: { userGroupId, ...pagination },
    queryConfig: { placeholderData: keepPreviousData }
  });

  if (error && !data) {
    return (
      <NoddiCard title={title} icon='MessageSquare' iconTile headerNode={headerNode} className={className}>
        <ApiErrorMessageInternal error={error} onRetry={() => void refetch()} isRetrying={isFetching} />
      </NoddiCard>
    );
  }

  if (isPending && !data) {
    return (
      <NoddiCard title={title} icon='MessageSquare' iconTile headerNode={headerNode} className={className}>
        <div className='flex items-center justify-center py-10'>
          <NoddiSpinner />
        </div>
      </NoddiCard>
    );
  }

  // Partner/staff timeline must not surface ops-only INTERNAL AI drafts (B8).
  const items = (data?.results ?? []).filter((item) => item.direction !== MessageDirection.INTERNAL);

  return (
    <NoddiCard
      title={title}
      icon='MessageSquare'
      iconTile
      description={description}
      headerNode={headerNode}
      className={className}
      contentClassName='flex flex-col gap-0 border-t border-border pt-0'
    >
      {error && (
        <div className='border-b border-border px-1 py-3'>
          <ApiErrorMessageInternal error={error} onRetry={() => void refetch()} isRetrying={isFetching} />
        </div>
      )}
      {items.length === 0 ? (
        <div className='px-1 py-6'>
          <NoddiEmptyContent
            size='small'
            emptyIcon='MessageSquare'
            title='No Support messages'
            message='Inbound and outbound Support messages for this customer will show up here'
          />
        </div>
      ) : (
        <ul className='-mx-5 flex flex-col'>
          {items.map((item, index) => (
            <TimelineRow key={item.id} item={item} isFirst={index === 0} linkToInbox={linkToInbox} />
          ))}
        </ul>
      )}
      {(data?.count ?? 0) > 0 && (
        <div className='border-t border-border px-1 pt-3'>
          <TablePagination
            pagination={pagination}
            totalCount={data?.count}
            loading={isFetching}
            onChange={(next) => setPagination({ ...next, search: pagination.search })}
          />
        </div>
      )}
    </NoddiCard>
  );
};

const TimelineRow = ({
  item,
  isFirst,
  linkToInbox
}: {
  item: SupportTimelineItemRecord;
  isFirst: boolean;
  linkToInbox: boolean;
}) => {
  const channel = supportChannelMeta(item.channel);
  const direction = supportDirectionMeta(item.direction);
  const stamp = noddiFormatDate(item.createdAt, NoddiDateFormats.DATE_TIME);

  const content = (
    <>
      <span className={channelBadgeVariants({ channel: item.channel })}>
        <NoddiIcon name={channel.icon} size='small' />
      </span>
      <span className='flex min-w-0 flex-1 flex-col gap-1.5'>
        <span className='flex flex-wrap items-baseline gap-x-2 gap-y-1'>
          <span className='text-sm font-semibold'>{item.conversationSubject || channel.label}</span>
          <NoddiBadge variant='outline' className='shrink-0'>
            {channel.label}
          </NoddiBadge>
          <NoddiBadge variant='outline' className='shrink-0'>
            {direction.label}
          </NoddiBadge>
          <span className='ml-auto shrink-0 text-xs text-muted-foreground'>{stamp}</span>
        </span>
        {item.body && (
          <span className='text-sm leading-relaxed whitespace-pre-wrap text-muted-foreground'>{item.body}</span>
        )}
      </span>
    </>
  );

  const rowClass = cn(
    'flex w-full items-start gap-4 px-5 py-3.5',
    !isFirst && 'border-t border-border',
    linkToInbox && 'hover:bg-accent'
  );

  if (!linkToInbox) {
    return (
      <li className={rowClass}>
        <span className='flex w-full items-start gap-4'>{content}</span>
      </li>
    );
  }

  return (
    <li>
      <NoddiLink
        to={adminRoutes.supportConversation.getPath({ id: item.conversationId })}
        variant='link'
        className={cn(rowClass, 'h-auto justify-start rounded-none no-underline hover:no-underline')}
      >
        {content}
      </NoddiLink>
    </li>
  );
};
